/**
 * Panel components.
 *
 * Presentation only. Every one receives fully-computed props and contains no
 * P&L maths — a redesign rewrites the view, not the calculation.
 */
import type { JSX } from 'react';
import type { Analysis, Analytics, RuleState, Trade } from '../lib/api.js';
import { fmtAgo, fmtNum, fmtPct, fmtTime, fmtUsd, pnlTone, signGlyph } from '../lib/api.js';
import { PortfolioRiskPanel } from './PortfolioRisk.js';
import { PrecedentPanel } from './Precedent.js';
import { NO_ZONE, type ZoneResolver } from '../lib/zone.js';
import { Link } from '../lib/router.js';
import { Icon } from './Icon.js';
import { EmptyState } from './EmptyState.js';

// ---------------------------------------------------------------------------

export function StatusDot({ state }: { state: string }): JSX.Element {
  const cls =
    state === 'open' ? 'ok' :
    state === 'maintenance_break' ? 'caution' : 'muted';
  return <span className={`dot dot-${cls}`} aria-hidden="true" />;
}

export function Pnl({ value, showSign = true }: { value: string | null; showSign?: boolean }): JSX.Element {
  const tone = pnlTone(value);
  return (
    <span className={`pnl pnl-${tone}`}>
      {/* Glyph so meaning is never carried by colour alone. */}
      <span className="pnl-glyph" aria-hidden="true">{signGlyph(value)}</span>
      {fmtUsd(value, { sign: showSign })}
    </span>
  );
}

// ---------------------------------------------------------------------------

export function AnalysisPanel({
  analysis, overlay, disclaimer, direction,
}: {
  analysis: Analysis | null;
  overlay: { tradeCount: number; winRate: string | null; netPnl: string; note: string } | null;
  disclaimer: string;
  /**
   * What the trader is actually holding in this symbol, when they hold
   * anything. It narrows the precedent to the side they are on — a long
   * record is not evidence about a short — and is absent when nothing is
   * open, which widens the mirror to both directions rather than guessing.
   */
  direction?: 'long' | 'short';
}): JSX.Element {
  if (!analysis) {
    return (
      <aside className="panel">
        <div className="panel-empty">Analysis has not been computed for this symbol yet.</div>
      </aside>
    );
  }

  const regimeLabel = analysis.regime.replace(/_/g, ' ');

  return (
    <aside className="panel" aria-label="AEGIS analysis">
      <div className="panel-head">
        <span className="badge badge-ai">◆ AEGIS ANALYSIS</span>
        {/* Staleness is always visible: broadcast analysis can be minutes old. */}
        <span className="panel-stale" title={new Date(analysis.computedAt * 1000).toISOString()}>
          {fmtAgo(analysis.computedAt)}
        </span>
      </div>

      <h2 className="panel-title">{regimeLabel}</h2>
      <div className="panel-meta">
        CONFIDENCE {analysis.confidence.toFixed(2)}
        {' · '}
        {analysis.hasModelLayer ? `MODEL ${analysis.modelId ?? ''}` : 'LAYER 1 ONLY'}
      </div>
      <div className="meter" role="img" aria-label={`Confidence ${analysis.confidence.toFixed(2)}`}>
        <i style={{ width: `${analysis.confidence * 100}%` }} />
      </div>

      <h3 className="panel-h">WHAT IS HAPPENING</h3>
      <p className="panel-body">{analysis.summary}</p>
      {/*
        The model runs on its cadence while Layer 1 refreshes on every bar, so
        the prose is routinely older than the levels. Saying so is not a
        disclaimer — a user must always know how old the read is.
      */}
      {analysis.hasModelLayer && analysis.modelComputedAt !== null && (
        <div
          className="panel-source"
          title={new Date(analysis.modelComputedAt * 1000).toISOString()}
        >
          Model read {fmtAgo(analysis.modelComputedAt)} · levels {fmtAgo(analysis.computedAt)}
        </div>
      )}

      <h3 className="panel-h">INVALIDATION</h3>
      <p className="panel-body panel-invalidation">{analysis.invalidation}</p>

      <h3 className="panel-h">EVIDENCE</h3>
      <ul className="evidence">
        {analysis.signals.slice(0, 6).map((s) => (
          <li key={s.id}>
            <span className="evidence-id">{s.id}</span>
            {s.price !== undefined && <span className="evidence-val">{fmtNum(s.price)}</span>}
          </li>
        ))}
      </ul>

      {/*
        The situation-aware mirror, below the plain per-symbol overlay because
        it is the narrower claim: the overlay is every ES trade, this is the
        ones taken in something like what is on screen now.
      */}
      <PrecedentPanel symbol={analysis.symbol} direction={direction} />

      {overlay && overlay.tradeCount > 0 && (
        <div className="overlay-card">
          <b>YOUR HISTORY</b>
          <p>
            {overlay.tradeCount} recorded {analysis.symbol} trades · win rate{' '}
            {fmtPct(overlay.winRate, 0)} · net <Pnl value={overlay.netPnl} />
          </p>
          <span className="overlay-note">
            Computed from your own journal. No model involved.
          </span>
        </div>
      )}

      <p className="disclaimer">{disclaimer}</p>
    </aside>
  );
}

// ---------------------------------------------------------------------------

export function RulePanel({ state }: { state: RuleState | null }): JSX.Element {
  if (!state) return <div className="panel-empty">No rule state.</div>;

  return (
    <section className="rules" aria-label="Account rules">
      <div className="rules-head">
        <div>
          <div className="rules-equity">{fmtUsd(state.equity)}</div>
          <div className="rules-sub">
            equity · day <Pnl value={state.realizedDayPnl} /> · open{' '}
            <Pnl value={state.unrealizedPnl} />
          </div>
        </div>
        <span className={`chip chip-${state.overall}`}>{state.overall.toUpperCase()}</span>
      </div>

      <div className="gauges">
        {state.checks.map((c) => (
          <div key={c.id} className="gauge">
            <div className="gauge-top">
              <span className="gauge-label">{c.label}</span>
              <span className={`gauge-value gauge-${c.status}`}>
                {c.headroom ? fmtUsd(c.headroom) : fmtNum(c.current, 0)}
              </span>
            </div>
            <div className={`meter meter-${c.status}`} role="img"
                 aria-label={`${c.label}: ${Math.round(c.utilisation * 100)}% used`}>
              <i style={{ width: `${Math.min(100, c.utilisation * 100)}%` }} />
            </div>
            <div className="gauge-msg">{c.message}</div>
          </div>
        ))}
      </div>

      {state.projected.length > 0 && (
        <div className="projected">
          <h3 className="panel-h">IF THE MARKET MOVES</h3>
          {state.projected.map((p, i) => (
            <div key={`${p.ruleId}-${p.symbol}-${i}`} className="projected-row">
              <span className="projected-points">{fmtNum(p.pointsUntilBreach, 2)} pts</span>
              <span className="projected-msg">
                {p.message}
                {/*
                  Stated beside the distance rather than in a settings page.
                  A trailing-intraday floor rises with a favourable move, so
                  going green does not buy room — and a trader reading only the
                  distance has no way to know which kind of account they are on.
                */}
                {p.ratchets && (
                  <span className="projected-ratchet">
                    This floor rises with a favourable move, so the distance does not
                    grow when the position goes your way.
                  </span>
                )}
                {p.withOneMore && (
                  <span className="projected-more">{p.withOneMore.message}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {/*
        The whole-book question the row above cannot answer.

        Each projected row assumes its symbol is the only one that moves. A
        correlated day spends the headroom once, and this is what that costs in
        the trader's own loss sizes rather than in an invented percentage.
      */}
      {/*
        Stated plainly, because it is the one line on this panel that is good
        news. A trailing account whose floor has stopped moving has quietly
        become a static one, and a trader who does not know that is managing
        risk against a constraint they no longer have.
      */}
      {/*
        Named rather than silently dropped. Every figure on this panel excludes
        these, and a trader shown a smaller book than they are holding is worse
        off than one shown an error.
      */}
      {state.unpriced.length > 0 && (
        <p className="unpriced">
          <strong>{state.unpriced.join(', ')}</strong> {state.unpriced.length === 1 ? 'is' : 'are'}
          {' '}not in the instrument master, so {state.unpriced.length === 1 ? 'it is' : 'they are'}
          {' '}excluded from every figure here. AEGIS will not guess a tick value.
        </p>
      )}

      {state.drawdownFrozen && (
        <p className="frozen">
          Your drawdown floor has stopped trailing. It no longer rises when the account
          makes a new high.
        </p>
      )}

      {state.scenario && (
        <div className="scenarios">
          <h3 className="panel-h">IF THIS GOES LIKE YOUR USUAL LOSERS</h3>
          <p className="scenario-msg">{state.scenario.summary}</p>

          <ul className="scenario-legs">
            {state.scenario.legs.map((l) => (
              <li key={l.symbol}>
                <span className="leg-sym">{l.symbol}</span>
                <span className="leg-move mono">{fmtNum(l.points, 2)} pts</span>
                <span className="leg-price mono">at {fmtNum(l.price, 2)}</span>
                <span className="leg-cost mono">−{fmtUsd(l.cost)}</span>
                {/* The sample, always. A figure without it cannot be weighed. */}
                <span className="leg-n">median of {l.sampleSize} losing trades</span>
              </li>
            ))}
          </ul>

          {/* One bad day, read against each rule it could reach. */}
          {state.scenario.against.map((a) => (
            <div key={a.ruleId} className={`scenario-out${a.breaches ? ' scenario-breaches' : ''}`}>
              {a.message}
            </div>
          ))}

          <p className="scenario-note">
            Your median losing trade on each instrument, at the size you are holding
            now, all on the same day. Counted from your journal — no model involved.
          </p>
        </div>
      )}

      {state.expiryWarnings.length > 0 && (
        <div className="expiry">
          <h3 className="panel-h">CONTRACT LIFECYCLE</h3>
          {state.expiryWarnings.map((w, i) => (
            <div key={i} className={`expiry-row expiry-${w.level}`}>{w.message}</div>
          ))}
        </div>
      )}

      {/*
        Across every account, and nothing at all for a single-account trader.
        Placed after the per-account gauges because it is a wider view of the
        same risk, not a replacement for it.
      */}
      <PortfolioRiskPanel />

      {state.positions.length > 0 && (
        <div className="positions">
          <h3 className="panel-h">OPEN POSITIONS</h3>
          {state.positions.map((p, i) => (
            <div key={i} className="position-row">
              <span className="position-sym">{p.symbol}</span>
              <span className={`position-dir dir-${p.direction}`}>
                {p.direction === 'long' ? 'LONG' : 'SHORT'} {Number(p.quantity)}
              </span>
              <span className="position-price">@ {p.displayEntry}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

/**
 * Win / loss / scratch, from the server.
 *
 * Not derived from the sign of `netPnl` here, because the breakeven band is
 * what decides and `analytics.ts` is the one place that applies it. A trade
 * that scratched at entry and paid $4 of commission is not a loss, and a table
 * that says it is teaches the trader to distrust the whole journal.
 */
function Outcome({ trade }: { trade: Trade }): JSX.Element {
  if (trade.isOpen) return <span className="outcome outcome-open">OPEN</span>;
  const o = trade.outcome ?? 'scratch';
  const label = o === 'win' ? 'WIN' : o === 'loss' ? 'LOSS' : 'BE';
  return <span className={`outcome outcome-${o}`}>{label}</span>;
}

/**
 * How far the trade went the wrong way before it went the right way.
 *
 * A column of these answers a question no single number does: *how many of my
 * winners were nearly losers?* Ten rows where the block sits far left of the
 * entry mark and the result is WIN is a specific and unflattering pattern, and
 * it is invisible in a P&L column.
 *
 * **Renders nothing when the geometry is null.** `reconstruct.ts` refuses to
 * report a misleading zero when bars do not cover a trade, and a flat bar here
 * would undo that by reading as "this trade took no heat".
 */
function ExcursionBar({ trade }: { trade: Trade }): JSX.Element {
  const g = trade.excursion;
  if (!g) return <span className="dim" aria-hidden="true">—</span>;

  const [from, to] = g.entryAt <= g.exitAt ? [g.entryAt, g.exitAt] : [g.exitAt, g.entryAt];
  const pct = (n: number) => `${(n * 100).toFixed(2)}%`;
  const tone = trade.outcome ?? 'scratch';

  return (
    <span
      className="exc"
      title={`Ran to ${fmtUsd(g.mfe)}, drew down to ${fmtUsd(g.mae)}, exited ${fmtUsd(g.exit)}`}
    >
      <span className="exc-track" aria-hidden="true">
        <span
          className={`exc-span exc-${tone}`}
          style={{ left: pct(from), width: pct(Math.max(to - from, 0.01)) }}
        />
        <span className="exc-entry" style={{ left: pct(g.entryAt) }} />
        <span className="exc-exit" style={{ left: pct(g.exitAt) }} />
      </span>
      <span className="sr-only">
        {`Ran to ${fmtUsd(g.mfe)}, drew down to ${fmtUsd(g.mae)}, exited at ${fmtUsd(g.exit)}.`}
      </span>
    </span>
  );
}

export function TradeTable({
  trades, onSelect, selectedId, zone = NO_ZONE,
}: {
  trades: Trade[];
  onSelect(t: Trade): void;
  selectedId: string | null;
  /**
   * Which zone these timestamps are rendered in, and what to call it.
   *
   * Named once in the header rather than appended to 400 rows, and never left
   * to be inferred: AEGIS has no default trading style, so the zone is the
   * user's preference and a table that does not say which one it used is
   * showing times that mean nothing in particular.
   *
   * A resolver rather than a string, because the default preference renders
   * each contract in its own exchange's zone. See `lib/zone.ts`.
   */
  zone?: ZoneResolver;
}): JSX.Element {
  if (trades.length === 0) {
    return (
      <EmptyState icon="journal" title="No trades recorded yet">
        <p>
          Trades appear here as your broker reports the fills — AEGIS reconstructs
          them, so there is nothing to enter by hand.
        </p>
      </EmptyState>
    );
  }

  return (
    <div className="table-wrap">
      <table className="trades">
        <thead>
          <tr>
            <th data-col="symbol">Symbol</th><th data-col="dir">Dir</th>
            <th data-col="result">Result</th><th className="num" data-col="qty">Qty</th>
            <th className="num" data-col="entry">Entry</th><th className="num" data-col="exit">Exit</th>
            <th className="num" data-col="net">Net P&amp;L</th><th className="num" data-col="r">R</th>
            <th className="num" data-col="mae">MAE</th><th className="num" data-col="mfe">MFE</th>
            <th data-col="excursion">Excursion</th>
            <th data-col="opened">Opened{zone.label ? <span className="th-zone"> ({zone.label})</span> : null}</th>
            <th data-col="open"><span className="sr-only">Open full view</span></th>
          </tr>
        </thead>
        <tbody>
          {trades.map((t) => (
            <tr
              key={t.id}
              onClick={() => onSelect(t)}
              className={selectedId === t.id ? 'selected' : ''}
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') onSelect(t); }}
            >
              <td className="mono" data-col="symbol">{t.symbol}</td>
              <td data-col="dir"><span className={`dir dir-${t.direction}`}>{t.direction === 'long' ? 'L' : 'S'}</span></td>
              <td data-col="result"><Outcome trade={t} /></td>
              <td className="num mono" data-col="qty">{Number(t.quantity)}</td>
              <td className="num mono" data-col="entry">{fmtNum(t.avgEntryPrice)}</td>
              <td className="num mono" data-col="exit">{t.avgExitPrice ? fmtNum(t.avgExitPrice) : '—'}</td>
              <td className="num" data-col="net"><Pnl value={t.netPnl} /></td>
              <td className="num mono" data-col="r">{t.rMultiple ? fmtNum(t.rMultiple, 2) : '—'}</td>
              <td className="num mono dim" data-col="mae">{t.mae ? fmtUsd(t.mae) : '—'}</td>
              <td className="num mono dim" data-col="mfe">{t.mfe ? fmtUsd(t.mfe) : '—'}</td>
              <td data-col="excursion"><ExcursionBar trade={t} /></td>
              <td className="mono dim" data-col="opened">{fmtTime(t.openedAt, zone.of(t.symbol))}</td>
              {/* The row itself opens the side panel, which is the fast path
                  while scanning. This is the deliberate one — full screen, the
                  execution sequence, the tags. `stopPropagation` so the two do
                  not fire together and leave a panel open behind the screen
                  the user asked for. */}
              <td data-col="open" onClick={(e) => e.stopPropagation()}>
                <Link to={`/trade/${encodeURIComponent(t.id)}`} className="row-open">
                  <Icon name="chevronRight" size="sm" />
                  <span className="sr-only">Open {t.symbol} in full</span>
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * A headline figure, with its evidence.
 *
 * **Every KPI carries either a micro-chart or an `n =`, and never neither.**
 * `Profit factor 1.82` computed from 400 trades and from 12 render identically
 * as text, and the difference is the whole question of whether to believe it.
 * `docs/18` §5 already requires evidence with every AI reading; a headline
 * statistic is no different.
 *
 * Not both, either — a card carrying a chart AND a count is a card that has
 * stopped choosing what its evidence is.
 */
function Kpi({ label, value, chart, n }: {
  label: string;
  value: JSX.Element | string;
  chart?: JSX.Element;
  /** Sample size, and what it counts. Rendered in mono under the figure. */
  n?: string;
}): JSX.Element {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {chart ?? (n ? <div className="kpi-n mono">{n}</div> : null)}
    </div>
  );
}

/**
 * Wins, scratches and losses as one bar, with the counts printed under it.
 *
 * The bar is the ratio and the counts are the sample. A donut alone would say
 * nothing a percentage does not; the numbers under it are the part that earns
 * the space.
 */
function OutcomeSplit({ win, scratch, loss }: {
  win: number; scratch: number; loss: number;
}): JSX.Element {
  const total = win + scratch + loss;
  if (total === 0) return <div className="kpi-n mono">no closed trades</div>;
  const pct = (n: number) => `${((n / total) * 100).toFixed(2)}%`;
  return (
    <div className="kpi-chart">
      <div className="split" aria-hidden="true">
        <span className="split-win" style={{ width: pct(win) }} />
        <span className="split-scratch" style={{ width: pct(scratch) }} />
        <span className="split-loss" style={{ width: pct(loss) }} />
      </div>
      <div className="kpi-n mono">
        <span className="up">{win}</span> · {scratch} · <span className="down">{loss}</span>
      </div>
    </div>
  );
}

/**
 * Average win against average loss, drawn to scale from a shared centre.
 *
 * Two dollar figures at the ends because the ratio alone hides the size: a 3:1
 * payoff on $30 wins and on $3,000 wins is the same ratio and not the same
 * trading.
 */
function WinLossBar({ avgWin, avgLoss }: { avgWin: string; avgLoss: string }): JSX.Element {
  const w = Math.abs(Number(avgWin));
  const l = Math.abs(Number(avgLoss));
  const span = w + l;
  if (!Number.isFinite(span) || span === 0) {
    return <div className="kpi-n mono">no closed trades</div>;
  }
  return (
    <div className="kpi-chart">
      <div className="winloss" aria-hidden="true">
        <span className="winloss-loss" style={{ width: `${((l / span) * 100).toFixed(2)}%` }} />
        <span className="winloss-win" style={{ width: `${((w / span) * 100).toFixed(2)}%` }} />
      </div>
      <div className="kpi-n mono">
        <span className="down">{fmtUsd(avgLoss)}</span>
        <span className="up">{fmtUsd(avgWin)}</span>
      </div>
    </div>
  );
}

export function AnalyticsPanel({ a }: { a: Analytics | null }): JSX.Element {
  // "No analytics." was a dead end: it did not say whether the user was waiting,
  // whether something had broken, or whether they were meant to do something.
  if (!a) {
    return (
      <EmptyState icon="analytics" title="No analytics yet">
        <p>
          Expectancy, win rate and R-multiples are computed from closed trades.
          They appear once this account has some.
        </p>
      </EmptyState>
    );
  }
  const s = a.summary;

  // Deliberately does NOT repeat the strip above. A figure printed twice on one
  // screen invites the reader to check whether the two agree, which is a worse
  // use of their attention than anything the duplicate could have told them.
  const stats: Array<[string, JSX.Element | string]> = [
    ['Avg win', <Pnl key="w" value={s.avgWin} />],
    ['Avg loss', <Pnl key="l" value={s.avgLoss} />],
    ['Largest win', <Pnl key="lw" value={s.largestWin} />],
    ['Largest loss', <Pnl key="ll" value={s.largestLoss} />],
    ['Max drawdown', <Pnl key="dd" value={a.maxDrawdown} />],
    ['Avg R', s.avgRMultiple ? fmtNum(s.avgRMultiple, 2) : 'unavailable'],
    ['Commissions', fmtUsd(s.commissions)],
    ['Win streak', String(s.maxWinStreak)],
    ['Loss streak', String(s.maxLossStreak)],
  ];

  const closed = s.winCount + s.lossCount + s.scratchCount;

  return (
    <div className="analytics">
      {/* The headline five, each with its evidence. The grid below is the
          reference list; this strip is what gets read at a glance. */}
      <div className="kpi-strip">
        <Kpi
          label="Net P&L"
          value={<Pnl value={s.netPnl} />}
          n={`n = ${closed} closed`}
        />
        <Kpi
          label="Win rate"
          value={fmtPct(s.winRate)}
          chart={<OutcomeSplit win={s.winCount} scratch={s.scratchCount} loss={s.lossCount} />}
        />
        <Kpi
          label="Profit factor"
          value={s.profitFactor ? fmtNum(s.profitFactor, 2) : '—'}
          // Counted against LOSSES, not trades: profit factor is gross wins
          // over gross losses, and with two losing trades the denominator is
          // the whole number. Saying "n = 400 trades" beside it would be
          // evidence for a figure that does not rest on it.
          n={s.profitFactor ? `over ${s.lossCount} losses` : 'no losses yet'}
        />
        <Kpi
          label="Expectancy"
          value={<Pnl value={s.expectancy} />}
          n={`per trade, n = ${closed}`}
        />
        <Kpi
          label="Avg win / loss"
          value={
            Number(s.avgLoss) === 0
              ? '—'
              : fmtNum(String(Math.abs(Number(s.avgWin) / Number(s.avgLoss))), 2)
          }
          chart={<WinLossBar avgWin={s.avgWin} avgLoss={s.avgLoss} />}
        />
      </div>

      <div className="stat-grid">
        {stats.map(([label, value]) => (
          <div key={label} className="stat">
            <div className="stat-label">{label}</div>
            <div className="stat-value">{value}</div>
          </div>
        ))}
      </div>

      <div className="breakdowns">
        {([['By instrument', a.byInstrument], ['By hour (exchange time)', a.byHourOfDay],
           ['By day of week', a.byDayOfWeek]] as const).map(([title, rows]) => (
          <div key={title} className="breakdown">
            <h3 className="panel-h">{title.toUpperCase()}</h3>
            {rows.slice(0, 8).map((b) => (
              <div key={b.key} className="breakdown-row">
                <span className="breakdown-key mono">{b.key}</span>
                <span className="breakdown-count dim">{b.tradeCount}</span>
                <span className="breakdown-wr dim">{fmtPct(b.winRate, 0)}</span>
                <Pnl value={b.netPnl} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
